import { ApiCustomer } from '@vend/types'
import { vn } from 'vend-number'
import { getFeatureFromCache } from '../../util/feature-cache'
import { FEATURE_ON_ACCOUNT_PAYMENT_TYPE } from '../../constants/wr-features'
import { isValidCustomer, getOutstandingBalance } from '../sale'
import {
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../customer'
import { ReduxCurrentSale } from '../../state/slices/currentSale/currentSaleSlice'

type ShouldDisableOnAccountPaymentParams = {
  customer: ApiCustomer | null | undefined
  amountTendered?: number | string
  sale: Pick<
    ReduxCurrentSale,
    'customerBalances' | 'payAccountBalance' | 'payments' | 'finals'
  >
}

/**
 * Evaluates the current sale and determines if the On Account payment option should be disabled.
 *
 * @method shouldDisableOnAccountPayment
 *
 * @param {ShouldDisableOnAccountPaymentParams} params
 *
 * @return {Boolean} True if the On Account payment option should be disabled, false otherwise.
 */
export const shouldDisableOnAccountPayment = ({
  customer,
  amountTendered,
  sale,
}: ShouldDisableOnAccountPaymentParams): boolean => {
  if (!getFeatureFromCache(FEATURE_ON_ACCOUNT_PAYMENT_TYPE)) {
    return true
  }

  if (!isValidCustomer(customer)) {
    return true
  }

  if (isOnAccountLimitDisabled(customer)) {
    return true
  }

  // Multiple On Account payments and mixed payment types are allowed.
  if (exceedsOutstandingBalance(amountTendered, sale)) {
    return true
  }

  if (!isOnAccountLimitUnlimited(customer)) {
    if (exceedsAvailableCredit(amountTendered, sale)) {
      return true
    }
  }

  return false
}

/**
 * Checks if the tendered amount exceeds the customer's available on-account credit.
 * When no amount is entered (0 or undefined), the button stays enabled as long as
 * the customer has any available credit remaining.
 */
const exceedsAvailableCredit = (
  amountTendered: number | string | undefined,
  sale: ShouldDisableOnAccountPaymentParams['sale']
): boolean => {
  const tendered = vn(amountTendered ?? 0)

  // Refunds return money to the account — credit check does not apply.
  if (tendered.lt(0)) {
    return false
  }

  const availableCredit = vn(
    sale.customerBalances?.availableOnAccountLimitBalance ?? 0
  )

  if (availableCredit.lte(0)) {
    return true
  }

  if (tendered.gt(0)) {
    return tendered.gt(availableCredit)
  }

  return false
}

/**
 * Checks if the tendered amount exceeds the sale's outstanding balance.
 * On Account should not allow overpayment — unlike Cash, there is no valid
 * scenario for giving physical change funded by a customer's credit line.
 */
const exceedsOutstandingBalance = (
  amountTendered: number | string | undefined,
  sale: ShouldDisableOnAccountPaymentParams['sale']
): boolean => {
  const tendered = vn(amountTendered ?? 0)

  if (tendered.lte(0)) {
    return false
  }

  const outstandingBalance = vn(getOutstandingBalance(sale))

  return tendered.gt(outstandingBalance)
}
